import { trackInternalEvent } from '@rio/tracking'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useI18n } from 'builder/hooks/useI18n'
import {
  PrefillResumeEvents,
  ResumeUploadStatus,
  ResumeUploadStep,
  ResumeValidationStatus,
} from 'builder/modules/constants'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { ALLOWED_FILES } from 'builder/components/FillResumeModal/constants'
import {
  actions,
  AutoTailoringFromPublicJobBoardMode,
  AutoTailoringFromPublicJobBoardStep,
  selectors,
} from 'builder/modules/resumeEditor'
import {
  ALLOWED_FILES_ARRAY,
  RESUME_MAX_SIZE,
} from 'builder/components/FillResumeModal/UploadResume/constants'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import UploadResumeIllustration from 'images/builder/fill-resume/upload-illustration.png'
import {
  Container,
  Description,
  Error,
  Img,
  Progress,
  ProgressBar,
  Title,
  UploadContainer,
} from './styles'
import { useProgressSimulation } from './useProgressSimulation'

export const UploadResumeCard = () => {
  const { i18n } = useI18n()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dispatch = useDispatch()
  const resume = useTypedSelector(selectors.resume)
  const [isDragging, setDragging] = useState(false)
  const { progress: fakeProgress, startProgress } = useProgressSimulation(15000, 1)
  let {
    resumeValidationStatus,
    selectedResume,
    resumeUploadStatus,
    validationError,
    errorSource,
    isSavingPrefilledResume,
  } = useTypedSelector(selectors.prefillResumeSection)

  const handleCardClick = () => {
    fileInputRef.current?.click()
  }
  const validateResume = useCallback(
    (selectedResume: File) => {
      if (selectedResume.size > RESUME_MAX_SIZE) {
        dispatch(actions.setResumeValidationStatus(ResumeValidationStatus.failed))
        dispatch(
          actions.setResumeValidationError({
            error: i18n.t(
              'builder.prefill_resume.auto_tailoring_view.upload_resume_card.file_size_error',
            ),
            errorSource: ResumeUploadStep.validation,
          }),
        )
      } else if (!ALLOWED_FILES_ARRAY.includes(selectedResume.type)) {
        dispatch(actions.setResumeValidationStatus(ResumeValidationStatus.failed))
        dispatch(
          actions.setResumeValidationError({
            error: i18n.t(
              'builder.prefill_resume.auto_tailoring_view.upload_resume_card.unsupported_file_type_error',
            ),
            errorSource: ResumeUploadStep.validation,
          }),
        )
      } else {
        trackInternalEvent(PrefillResumeEvents.completeResumeUpload)
        dispatch(actions.setResumeValidationStatus(ResumeValidationStatus.validated))
        dispatch(
          actions.setResumeValidationError({
            error: null,
            errorSource: null,
          }),
        )
      }
    },
    [dispatch, i18n],
  )

  useEffect(() => {
    if (resumeUploadStatus === ResumeUploadStatus.failed) {
      dispatch(
        actions.setResumeValidationError({
          error: i18n.t(
            'builder.prefill_resume.auto_tailoring_view.upload_resume_card.generic_error',
          ),
          errorSource: ResumeUploadStep.validation,
        }),
      )
    }
  }, [resumeUploadStatus, dispatch, i18n])

  const handleResumeInput = (selectedResume: File | null) => {
    if (!selectedResume) {
      return
    }
    trackInternalEvent(PrefillResumeEvents.clickUploadResume)
    dispatch(actions.setResumeValidationStatus(ResumeValidationStatus.uploading))
    dispatch(actions.setResumeForPrefill(selectedResume))
    validateResume(selectedResume)
  }

  const handleFileInputDragChange = (event: React.DragEvent<HTMLInputElement>) => {
    event.preventDefault()
    setDragging(false)

    const files = event.dataTransfer.files
    if (files.length > 0) {
      const selectedResume = files[0]
      handleResumeInput(selectedResume)
    }
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedResume = event.target.files?.[0] || null
    handleResumeInput(selectedResume)
  }
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    if (errorSource === ResumeUploadStep.validation) {
      setIsProcessing(false)
      trackInternalEvent(PrefillResumeEvents.seeResumeUploadError, { error: validationError })
    }
    if (errorSource === ResumeUploadStep.parsing) {
      setIsProcessing(false)
      trackInternalEvent(PrefillResumeEvents.seeResumeParsingError)
    }
  }, [errorSource, validationError])

  useEffect(() => {
    if (isProcessing && !isSavingPrefilledResume) {
      dispatch(
        actions.setAutoTailoringForPublicJobBoard({
          step: AutoTailoringFromPublicJobBoardStep.step1,
          mode: AutoTailoringFromPublicJobBoardMode.reviewResume,
          enableAutoTailoringButton: true,
        }),
      )
    }
  }, [isSavingPrefilledResume, dispatch, isProcessing])

  useEffect(() => {
    if (resumeValidationStatus === ResumeValidationStatus.validated && selectedResume && resume) {
      setIsProcessing(true)
      startProgress()
      dispatch(
        actions.parseAndSavePrefilledResume({
          selectedResume,
          resumeId: resume.id,
          keepResumeData: true,
        }),
      )
    }
  }, [resumeValidationStatus, dispatch, resume, selectedResume, startProgress])

  const { isPhone } = useMediaQueries()

  if (
    resumeValidationStatus === ResumeValidationStatus.uploading ||
    resumeValidationStatus === ResumeValidationStatus.validated ||
    isProcessing
  ) {
    return (
      <Container $disableHover>
        <UploadContainer>
          <Title $disableHover>
            {i18n.t('builder.prefill_resume.auto_tailoring_view.upload_resume_card.uploading')}
          </Title>
          <Progress>
            <ProgressBar progress={fakeProgress} />
          </Progress>
        </UploadContainer>
      </Container>
    )
  }

  return (
    <>
      <Container
        onClick={handleCardClick}
        $hasError={!!validationError}
        $isDragging={isDragging}
        onDrop={handleFileInputDragChange}
        onDragOver={e => {
          e.preventDefault()
          setDragging(true)
        }}
      >
        <Img src={UploadResumeIllustration} />
        <div>
          <Title>
            {i18n.t(
              `builder.prefill_resume.auto_tailoring_view.upload_resume_card.title${
                isPhone ? '_mobile' : ''
              }`,
            )}
          </Title>
          <Description>
            {i18n.t('builder.prefill_resume.auto_tailoring_view.upload_resume_card.description')}
          </Description>
        </div>
        <input
          style={{ display: 'none' }}
          type="file"
          onChange={handleFileInputChange}
          accept={ALLOWED_FILES}
          ref={fileInputRef}
        />
      </Container>
      {validationError && <Error>{validationError}</Error>}
    </>
  )
  return null
}
